import React from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import styled from 'styled-components';
import styles from '@utils/styles';

import breakpoints from '@repo/ui-components/breakpoints';
import BreadcrumbWithWrapper from '@repo/ui-components/BreadcrumbWithWrapper';
import BlockBuilder from '@repo/ui-components/sanity/BlockBuilder';
import GridCard from '@repo/ui-components/GridCard';
import GridCardContainer from '@repo/ui-components/GridCardContainer';
import FrontMatterImage from '@repo/ui-components/sanity/types/FrontMatterImage';
import PageWrapper from '@repo/ui-components/page-wrapper/PageWrapper';
import sharedStyles from '@repo/ui-components/styles';

import getMultipleRandomItemsFromArray from '@repo/utils/getMultipleRandomItemsFromArray';
import withTwoPassRendering from '@repo/utils/twoPassRendering';
import createOpenGraphTags from '@repo/utils/createOpenGraphTags';

import GearTagLine from '@components/GearTagLine';
import Layout from '@components/layout';
import {
  getLocalizedPagePath,
  LocalizedLink,
} from '@repo/gatsby-localization-helpers';
import { useSportdogInternationalContext } from '@util/static-translations';

const GridCardWithTwoPass = withTwoPassRendering(GridCard);

const ProfileContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;

  img {
    width: 100%;
    display: block;
    margin-bottom: ${sharedStyles.spacing['spacing-md']};
  }

  @media (min-width: ${breakpoints.md}) {
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: space-between;
  }
`;

const Location = styled.p`
  color: white;
  font-size: ${sharedStyles.fontSize.lg};
  font-weight: 700;
  line-height: inherit;
`;

const LeftColumn = styled.div`
  width: 100%;

  @media (min-width: ${breakpoints.md}) {
    width: 25%;
  }
`;

const RightColumn = styled.div`
  width: 100%;

  @media (min-width: ${breakpoints.md}) {
    width: 73%;
  }
`;

const WebsiteLink = styled.a`
  text-decoration: underline;
  font-weight: 600;
  color: ${styles.colors.orange[500]};

  &:hover {
    text-decoration: none;
  }
`;

const ArticlesContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  margin-top: ${sharedStyles.spacing['spacing-1xl']};
`;

const ArticlesAuthor = styled.h2`
  line-height: 1.15;
  margin: 0 0 ${sharedStyles.spacing['spacing-2xs']};
  text-transform: uppercase;
`;

const ViewAllArticlesLink = styled(LocalizedLink)`
  display: inline-block;
  font-size: 24px;
  min-height: 14px;
  margin-top: 20px;
  padding-right: 25px;
  text-decoration: underline;
  text-transform: uppercase;
  font-weight: 400;
  color: ${styles.colors.orange[500]};

  &:hover {
    text-decoration: none;
  }
`;

function ProstaffProfile({ pageContext, data }) {
  const {
    SEOMetadata,
    title,
    description,
    image,
    slug,
    location,
    biography,
    links,
    relatedArticles,
    personCategory,
  } = data?.sanityPerson ?? {};

  const { linkHreflangValues } = pageContext;

  const { tt, locale } = useSportdogInternationalContext();

  const breadcrumbs = [{ text: tt(s => s.breadcrumbsCommon.home), link: '/' }];

  if (personCategory?.slug) {
    breadcrumbs.push({
      text: personCategory.title,
      link: `/about-us/pro-staff/${personCategory?.slug?.current}`,
    });
  }

  breadcrumbs.push({
    text: title,
  });

  const otherStaff = getMultipleRandomItemsFromArray(
    data.people.nodes.filter(person => person?.slug?.current !== slug?.current),
    3
  );

  return (
    <Layout
      headTagData={{
        title: SEOMetadata?.title || title,
        description: SEOMetadata?.description || description,
        canonicalUrl: SEOMetadata?.canonicalUrl,
        noIndex: SEOMetadata?.noIndex,
        noFollow: SEOMetadata?.noFollow,
        meta: createOpenGraphTags(
          {
            title: SEOMetadata?.ogTitle || SEOMetadata?.title || title,
            description:
              SEOMetadata?.ogDescription ||
              SEOMetadata?.description ||
              description,
            url:
              SEOMetadata?.ogUrl ||
              SEOMetadata?.canonicalUrl ||
              (typeof window !== 'undefined' && window?.location?.pathname) ||
              getLocalizedPagePath('/', locale),
            image: SEOMetadata?.ogImage || image || '',
          },
          'sportdog'
        ),
      }}
      linkHreflangValues={linkHreflangValues}
    >
      <BreadcrumbWithWrapper elements={breadcrumbs} />

      <PageWrapper>
        <ProfileContainer>
          <LeftColumn>
            {image?.asset && <FrontMatterImage value={image} />}
          </LeftColumn>
          <RightColumn>
            {title && <h1>{title}</h1>}

            {location && <Location>{location}</Location>}

            {biography && <BlockBuilder blocks={biography} />}

            {links?.length > 0 && (
              <>
                <h2>{tt(s => s.prostaffPage.websiteLinksHeader)}</h2>
                {links.map(link => (
                  <WebsiteLink
                    key={link}
                    href={link}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {link}
                  </WebsiteLink>
                ))}
              </>
            )}
          </RightColumn>

          {relatedArticles?.length > 0 && (
            <>
              <ArticlesContainer>
                <ArticlesAuthor>
                  {tt(s => s.prostaffPage.articlesBy, { title })}
                </ArticlesAuthor>
                <GridCardContainer>
                  {relatedArticles?.slice(0, 3)?.map(article => (
                    <GridCard
                      key={article.title}
                      item={{
                        ...article,
                        link: `/hunting-training-tips/${article.slug.current}`,
                      }}
                    />
                  ))}
                </GridCardContainer>
              </ArticlesContainer>

              {relatedArticles.length > 3 && (
                <ViewAllArticlesLink
                  to={`/hunting-training-tips/authors/${slug?.current}`}
                >
                  {tt(s => s.prostaffPage.allArticlesBy, { title })}
                </ViewAllArticlesLink>
              )}
            </>
          )}

          {otherStaff?.length > 0 && (
            <ArticlesContainer>
              <ArticlesAuthor>
                {tt(s => s.prostaffPage.otherProstaff)}
              </ArticlesAuthor>
              <GridCardContainer>
                {otherStaff.map(prostaff => (
                  <GridCardWithTwoPass
                    key={prostaff.slug.current}
                    item={{
                      ...prostaff,
                      link: `/about-us/pro-staff/${prostaff.slug.current}`,
                    }}
                  />
                ))}
              </GridCardContainer>
            </ArticlesContainer>
          )}
        </ProfileContainer>
      </PageWrapper>
      <GearTagLine />
    </Layout>
  );
}

export const query = graphql`
  query SanityPersonQuery($_id: String, $locale: String) {
    sanityPerson(_id: { eq: $_id }) {
      SEOMetadata {
        title
        description
        canonicalUrl
        noIndex
        noFollow
        ogTitle
        ogDescription
        ogUrl
        ogImage {
          asset {
            url
          }
        }
      }
      title
      language
      description
      slug {
        current
      }
      image {
        asset: _rawAsset(resolveReferences: { maxDepth: 3 })
        crop: _rawCrop(resolveReferences: { maxDepth: 3 })
        hotspot: _rawHotspot(resolveReferences: { maxDepth: 3 })
        alt
      }
      personCategory: localizedPersonCategoryValue {
        title
        slug {
          current
        }
      }
      location
      occupation
      biography: _rawBiography(resolveReferences: { maxDepth: 3 })
      links
      relatedArticles {
        title
        slug {
          current
        }
        image {
          asset: _rawAsset(resolveReferences: { maxDepth: 3 })
          crop: _rawCrop(resolveReferences: { maxDepth: 3 })
          hotspot: _rawHotspot(resolveReferences: { maxDepth: 3 })
          alt
        }
        description
      }
    }

    people: allSanityPerson(
      filter: { siteIdentifier: { eq: "sportdog" }, language: { eq: $locale } }
    ) {
      nodes {
        language
        _id
        slug {
          current
        }
        title
        description
        slug {
          current
        }
        image {
          asset: _rawAsset(resolveReferences: { maxDepth: 3 })
          crop: _rawCrop(resolveReferences: { maxDepth: 3 })
          hotspot: _rawHotspot(resolveReferences: { maxDepth: 3 })
          alt
        }
      }
    }
  }
`;

ProstaffProfile.propTypes = {
  data: PropTypes.shape({
    sanityPerson: PropTypes.shape({
      SEOMetadata: PropTypes.shape({
        title: PropTypes.string,
        description: PropTypes.string,
        canonicalUrl: PropTypes.string,
        noIndex: PropTypes.bool,
        noFollow: PropTypes.bool,
        ogTitle: PropTypes.string,
        ogDescription: PropTypes.string,
        ogUrl: PropTypes.string,
        ogImage: PropTypes.shape({
          asset: PropTypes.shape({
            url: PropTypes.string,
          }),
        }),
      }),
      title: PropTypes.string,
      language: PropTypes.string,
      description: PropTypes.string,
      image: PropTypes.shape({
        asset: PropTypes.shape({
          url: PropTypes.string,
        }),
        alt: PropTypes.string,
      }),
      slug: PropTypes.shape({
        current: PropTypes.string,
      }),
      location: PropTypes.string,
      biography: PropTypes.arrayOf(PropTypes.shape({})),
      links: PropTypes.arrayOf(PropTypes.string),
      relatedArticles: PropTypes.arrayOf(PropTypes.shape({})),
    }),
    people: { nodes: PropTypes.arrayOf({ _id: PropTypes.string }) },
  }).isRequired,
  pageContext: PropTypes.shape({
    personCategory: PropTypes.shape({
      title: PropTypes.string,
      slug: PropTypes.shape({
        current: PropTypes.string,
      }),
    }),
    linkHreflangValues: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
};

export default ProstaffProfile;
